import React, { useCallback, useEffect, useState } from 'react'
import { EditOutlined, PlusSquareOutlined } from '@ant-design/icons'
import { Button, Card, Drawer, Form, Space, Table, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TablePaginationConfig } from 'antd/es/table'
import moment from 'moment'

import { Shrine } from '@a4b/api/src/modules/Temple/types'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import { PUJA_PRESIGN_URL, YODA_CDN_URL } from '@/utils/constants'
import TithiForm from './components/TithiForm'
import {
  formatTithiCreatePayload,
  formatTithiUpdatePayload,
  parseTithiToFormFields,
} from './utils/helper'

type TithiRow = Shrine.Tithi

const ManageTithi: React.FC = () => {
  const { networkInstance } = useAppContext()
  const [form] = Form.useForm()

  const cdnUrl = YODA_CDN_URL
  const preSignUrl = PUJA_PRESIGN_URL

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tithiList, setTithiList] = useState<TithiRow[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingTithi, setEditingTithi] = useState<TithiRow | null>(null)
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  // Fetch Tithis
  const fetchTithis = useCallback(async () => {
    setLoading(true)
    try {
      const response =
        await networkInstance.clientWithHeaders.shrineApi.getTithis()
      if (response?.data?.data) {
        setTithiList(response.data.data)
        setPagination(prev => ({ ...prev, total: response.data.data.length }))
      }
    } catch (error) {
      message.error('Failed to fetch tithis')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [networkInstance])

  useEffect(() => {
    fetchTithis()
  }, [fetchTithis])

  // Create Tithi
  const handleCreate = useCallback(
    async (values: any) => {
      if (submitting) return
      setSubmitting(true)
      try {
        const payload = formatTithiCreatePayload(values)
        await networkInstance.clientWithHeaders.shrineApi.createTithi(payload)
        message.success('Tithi created successfully')
        setDrawerOpen(false)
        form.resetFields()
        fetchTithis()
      } catch (error) {
        message.error('Failed to create tithi')
        console.error(error)
      } finally {
        setSubmitting(false)
      }
    },
    [networkInstance, form, fetchTithis, submitting],
  )

  // Update Tithi
  const handleUpdate = useCallback(
    async (values: any) => {
      if (!editingTithi?.id) return
      if (submitting) return
      setSubmitting(true)
      try {
        const payload = formatTithiUpdatePayload(values, editingTithi.id)
        await networkInstance.clientWithHeaders.shrineApi.updateTithi(
          editingTithi.id,
          payload,
        )
        message.success('Tithi updated successfully')
        setDrawerOpen(false)
        setEditingTithi(null)
        form.resetFields()
        fetchTithis()
      } catch (error) {
        message.error('Failed to update tithi')
        console.error(error)
      } finally {
        setSubmitting(false)
      }
    },
    [networkInstance, editingTithi, form, fetchTithis, submitting],
  )

  // Form Submit
  const handleSubmit = useCallback(
    async (values: any) => {
      if (editingTithi) {
        await handleUpdate(values)
      } else {
        await handleCreate(values)
      }
    },
    [editingTithi, handleCreate, handleUpdate],
  )

  // Open Create Drawer
  const openCreate = useCallback(() => {
    setEditingTithi(null)
    form.resetFields()
    setDrawerOpen(true)
  }, [form])

  // Open Edit Drawer
  const openEdit = useCallback(
    (record: TithiRow) => {
      setEditingTithi(record)
      const formFields = parseTithiToFormFields(record)
      form.setFieldsValue(formFields)
      setDrawerOpen(true)
    },
    [form],
  )

  // Close Drawer
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    setEditingTithi(null)
    form.resetFields()
  }, [form])

  // Table Columns
  const columns: ColumnsType<TithiRow> = [
    {
      title: 'S.No',
      key: 'index',
      width: 70,
      render: (_, __, index) =>
        (pagination.current! - 1) * pagination.pageSize! + index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Tithi Type',
      dataIndex: 'tithi_type',
      key: 'tithi_type',
      width: 120,
    },
    {
      title: 'Display Name (Hindi)',
      dataIndex: 'display_name_hi',
      key: 'display_name_hi',
      width: 180,
    },
    {
      title: 'Display Name (English)',
      dataIndex: 'display_name_en',
      key: 'display_name_en',
      width: 180,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: text => (text ? moment(text).format('DD MMM YYYY, HH:mm') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination)
  }

  return (
    <Card
      className='card-flex-column-grow'
      title='Shraddha Shorts - Tithi'
      extra={
        <Button
          type='primary'
          icon={<PlusSquareOutlined />}
          onClick={openCreate}
        >
          Add New Tithi
        </Button>
      }
    >
      <div className='full-size-flex-column'>
        <Table<TithiRow>
          rowKey='id'
          loading={loading}
          columns={columns}
          dataSource={tithiList}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />

        <Drawer
          title={editingTithi ? 'Edit Tithi' : 'Create Tithi'}
          width={600}
          visible={drawerOpen}
          onClose={closeDrawer}
          destroyOnClose
        >
          <TithiForm
            form={form}
            onFinish={handleSubmit}
            onCancel={closeDrawer}
            isEditing={!!editingTithi}
            cdnUrl={cdnUrl}
            preSignUrl={preSignUrl}
            submitting={submitting}
          />
        </Drawer>
      </div>
    </Card>
  )
}

export default withDefaultLayout(ManageTithi)
